import React, { useEffect, useState } from 'react';
import NewModal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import { useSelector } from 'react-redux';
import ReusableMultiSelectDialog from '../../components/ReusableMultiSelectDialog';
import { getAllCategory } from '../../actions';

const useFilePreview = (file) => {
    const [url, setUrl] = useState('');

    useEffect(() => {
        if (typeof File === "undefined" || !(file instanceof File)) {
            setUrl('');
            return undefined;
        }

        const objectUrl = URL.createObjectURL(file);
        setUrl(objectUrl);

        return () => URL.revokeObjectURL(objectUrl);
    }, [file]);

    return url;
};

const mediaBoxStyle = {
    marginTop: 14,
    padding: 12,
    border: '1px solid rgba(0,0,0,.14)',
    borderRadius: 10,
};

const mediaActionsStyle = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
};

const previewStyle = {
    display: 'block',
    width: '100%',
    maxWidth: 360,
    height: 140,
    marginTop: 10,
    borderRadius: 8,
    objectFit: 'cover',
    background: '#f3f4f6',
};

const OfficeFormModal = ({
    show,
    handleClose,
    onSubmit,
    currentId,
    formItem,
    setFormItem,
}) => {
    const categories = useSelector(
        (state) => state.category.categories.filter((cat) => cat.visibleCategory)
    );

    const coverPreview = useFilePreview(formItem.coverImageFile);
    const watermarkPreview = useFilePreview(formItem.watermarkImageFile);

    const handleCategorySubmit = () => {
        handleClose();
    };

    const categoryList = categories[0]?.children;

    const patch = (next) => {
        setFormItem((previous) => ({
            ...previous,
            ...next,
        }));
    };

    const onCoverPick = (event) => {
        const file = event?.target?.files?.[0];

        if (!file) return;

        patch({
            coverImageFile: file,
            removeCoverImage: false,
        });

        event.target.value = '';
    };

    const onWatermarkPick = (event) => {
        const file = event?.target?.files?.[0];

        if (!file) return;

        patch({
            watermarkImageFile: file,
            removeWatermarkImage: false,
        });

        event.target.value = '';
    };

    return (
        <NewModal
            show={show}
            handleClose={handleClose}
            modalTitle={currentId ? 'Редактировать офис' : 'Добавить офис'}
            onSubmit={onSubmit}
            currentId={currentId}
            disabledSubmit={!formItem.name || !formItem.address}
        >
            <Input
                label="Название офиса"
                value={formItem.name}
                placeholder="Введите название"
                onChange={(e) => patch({ name: e.target.value })}
            />

            <Input
                label="Адрес офиса"
                value={formItem.address}
                placeholder="Введите адрес"
                onChange={(e) => patch({ address: e.target.value })}
            />

            {currentId ? (
                <Input
                    label="Кредитный лимит офиса, ₽"
                    type="number"
                    value={formItem.creditLimit ?? 3000}
                    placeholder="3000"
                    onChange={(e) => patch({ creditLimit: Math.max(0, Number(e.target.value || 0)) })}
                />
            ) : null}

            <div style={mediaBoxStyle}>
                <div style={{ fontWeight: 700 }}>Цвет офиса</div>

                <div style={mediaActionsStyle}>
                    <input
                        type="color"
                        value={formItem.accentColor || '#6366f1'}
                        onChange={(event) => patch({ accentColor: event.target.value })}
                    />
                    <code>{formItem.accentColor || '#6366f1'}</code>
                </div>
            </div>

            <div style={mediaBoxStyle}>
                <div style={{ fontWeight: 700 }}>Фон офиса</div>

                <div style={mediaActionsStyle}>
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onCoverPick}
                    />

                    {(formItem.coverImage || formItem.coverImageFile) && !formItem.removeCoverImage ? (
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => patch({
                                coverImageFile: null,
                                removeCoverImage: true,
                            })}
                        >
                            Удалить фон
                        </button>
                    ) : null}
                </div>

                {coverPreview ? (
                    <img src={coverPreview} alt="Предпросмотр фона офиса" style={previewStyle} />
                ) : formItem.coverImage && !formItem.removeCoverImage ? (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                        Текущий файл: {formItem.coverImage.originalName || String(formItem.coverImage._id || '')}
                    </div>
                ) : null}
            </div>

            <div style={mediaBoxStyle}>
                <div style={{ fontWeight: 700 }}>Водяной знак</div>

                <div style={mediaActionsStyle}>
                    <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onWatermarkPick}
                    />

                    {(
                        formItem.watermarkMedia ||
                        formItem.watermarkImage ||
                        formItem.watermarkImageFile
                    ) && !formItem.removeWatermarkImage ? (
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => patch({
                                watermarkImageFile: null,
                                watermarkImage: '',
                                removeWatermarkImage: true,
                            })}
                        >
                            Удалить watermark
                        </button>
                    ) : null}
                </div>

                {watermarkPreview ? (
                    <img
                        src={watermarkPreview}
                        alt="Предпросмотр водяного знака"
                        style={{ ...previewStyle, objectFit: 'contain' }}
                    />
                ) : formItem.watermarkMedia && !formItem.removeWatermarkImage ? (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                        Текущий файл: {formItem.watermarkMedia.originalName || String(formItem.watermarkMedia._id || '')}
                    </div>
                ) : formItem.watermarkImage && !formItem.removeWatermarkImage ? (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                        Есть legacy watermark. При следующей замене он будет перенесён в приватное хранилище.
                    </div>
                ) : null}
            </div>

            <ReusableMultiSelectDialog
                handleClickBottomVisible={false}
                type="category"
                title="Выберите категории"
                description="Выберите категории, которые будут доступны для этого офиса"
                data={categoryList}
                selectedValues={formItem.categories}
                setSelectedValues={(values) => patch({ categories: values })}
                onSubmit={handleCategorySubmit}
                fetchData={getAllCategory}
                buttonText="Выбрать категории"
                inputLabelText="категории"
            />
        </NewModal>
    );
};

export default OfficeFormModal;
